import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { ArrowDownToLine } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, LoadingBlock, EmptyState, formatDateTime } from "@/components/Common";
import { useI18n } from "@/context/I18nContext";
import api, { formatApiError } from "@/lib/api";

const STATUSES = ["CREATED", "AWAITING_PAYMENT", "DETECTED", "CONFIRMING", "PENDING_REVIEW", "CONFIRMED", "REJECTED", "CANCELLED"];

export default function AdminDeposits() {
  const { lang } = useI18n();
  const L = (it, en, de) => (lang === "en" ? en : lang === "de" ? (de ?? en ?? it) : it);
  const [items, setItems] = useState(null);
  const [status, setStatus] = useState("");
  const load = useCallback(async () => {
    const { data } = await api.get("/deposits", { params: { status: status || undefined } });
    setItems(data);
  }, [status]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const update = async (d, body) => {
    try { await api.patch(`/admin/deposits/${d.id}`, body); toast.success(L("Aggiornato", "Updated", "Aktualisiert")); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <h2 className="font-heading text-2xl font-medium text-white">{L("Depositi", "Deposits", "Einzahlungen")}</h2>
      <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-10 rounded-md bg-navy-paper border border-white/10 px-3 text-sm text-white max-w-xs" data-testid="dep-status-filter"><option value="">{L("Tutti", "All", "Alle")}</option>{STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}</select>
      {items === null ? <LoadingBlock /> : items.length === 0 ? <EmptyState icon={ArrowDownToLine} title={L("Nessun deposito", "No deposits", "Keine Einzahlungen")} /> : (
        <div className="space-y-3">
          {items.map((d) => (
            <Card key={d.id} className="p-5" data-testid={`admin-dep-${d.id}`}>
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <p className="text-white text-sm">{d.amount} {d.asset || ""} · {d.method}</p>
                  {d.tx_hash && <p className="text-xs font-mono text-slate-500 break-all mt-1">{d.tx_hash}</p>}
                  <p className="text-xs text-slate-600 mt-1">{formatDateTime(d.created_at)} · <span className="text-gold">{d.status}</span> · {L("conferme", "confirmations", "Bestätigungen")}: {d.confirmations}</p>
                </div>
                {["CREATED", "AWAITING_PAYMENT", "DETECTED", "CONFIRMING", "PENDING_REVIEW"].includes(d.status) && (
                  <DepositActions d={d} onUpdate={update} L={L} />
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function DepositActions({ d, onUpdate, L }) {
  const [conf, setConf] = useState(d.confirmations || 0);
  const [note, setNote] = useState("");
  return (
    <div className="flex flex-col gap-2 w-full sm:w-auto">
      <div className="flex gap-2">
        <Input type="number" value={conf} onChange={(e) => setConf(e.target.value)} className="bg-navy border-white/10 h-8 w-24" placeholder={L("Conferme", "Confirmations", "Bestätigungen")} data-testid={`dep-conf-${d.id}`} />
        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-500 h-8" onClick={() => onUpdate(d, { status: "CONFIRMED", confirmations: parseInt(conf) || 0, review_notes: note })} data-testid={`dep-confirm-${d.id}`}>{L("Conferma", "Confirm", "Bestätigen")}</Button>
        <Button size="sm" variant="outline" className="border-red-500/40 text-red-400 h-8" onClick={() => onUpdate(d, { status: "REJECTED", review_notes: note })} data-testid={`dep-reject-${d.id}`}>{L("Rifiuta", "Reject", "Ablehnen")}</Button>
      </div>
      <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder={L("Note interne", "Internal notes", "Interne Notizen")} className="bg-navy border-white/10 h-8" />
    </div>
  );
}
